import {CacheKeyConfig, useGrpcRequest} from '@aboutyou/data-fetcher';
import {ProductImageTypes} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_images.types';
import {ArticleDetailService_GetProductBulkLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.grpc.lazy';
import {GetProductBulkResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useIsInitialLocation, useRouteEntities} from '@aboutyou/router';
import {useBasketToken, useBasketTokenAwaitedAnySession} from '@aboutyou/user';
import {useEffect} from 'react';
import {useActivateADPLandingPageApiTest} from '../../client-services/InitializeAbTestsConditionally/aa_tests_for_testing_the_setup';
import {useActivatedCouponState} from '../../features/Coupons/useActivatedCouponState';
import {useCouponPromotionIdFromQueryParams} from '../../features/Coupons/useCouponPromotionIdFromQueryParams';
import type {ProductTileEntities} from '../../features/ProductTile/types';
import {useProductIdFromSlug} from '../../features/ProductUrl';
import {useReductionsStateRequestParameter} from '../../features/ReductionsState/useReductionsStateRequestParameter';
import {
  SelectedSize,
  SetSelectedSize,
} from '../../features/SizeSelection/hooks/useSelectedSizeState';
import {
  getProtoProductImageType,
  useProductImageTypeCallback,
} from '../../shared-deprecated/useProductImageType';
import {useSelectedTargetGroup} from '../../shared-deprecated/useSelectedTargetGroup';
import {useIsQuickView} from './QuickView/useIsQuickView';
import {getCurrentSelectedSizeFromSizesSection} from './utils/getCurrentSelectedSizeFromSizesSection';
import {useGetBadgesFromBulkRequest} from './utils/useGetBadgesFromBulkRequest';
import {useKeepPreviousData} from './utils/useKeepPreviousData';

const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;

const useGetProductBulkSection = <
  Section extends keyof GetProductBulkResponse,
>({
  section,
  onSuccess,
  disabled,
  productId: productIdFromProps,
}: {
  section: Section;
  onSuccess?: (data: GetProductBulkResponse) => void;
  disabled?: boolean;
  productId?: number;
}) => {
  const preferredProductImage = usePreferredProductImage();
  const {targetGroup} = useSelectedTargetGroup();
  const productIdFromSlug = useProductIdFromSlug();
  const productId = productIdFromProps || productIdFromSlug;

  const {token: basketToken} = useBasketToken();
  const {session, refetchesDisabled} = useBasketTokenAwaitedAnySession();

  const keepPreviousData = useKeepPreviousData({
    productId,
    session,
  });

  const isInitialLocation = useIsInitialLocation();
  const activateAATest = useActivateADPLandingPageApiTest();

  const {couponPromotionId: activatedCouponPromotionId} =
    useActivatedCouponState(productId);
  const couponPromotionIdFromQueryParams =
    useCouponPromotionIdFromQueryParams();
  const selectedCouponPromotionId =
    couponPromotionIdFromQueryParams || activatedCouponPromotionId;

  const request = useGrpcRequest(
    ArticleDetailService_GetProductBulkLazy,
    {
      productId: productId!,
      session,
      targetGroup,
      enableSizeFinder: true,
      preferredProductImage,
      reductionsState: useReductionsStateRequestParameter(),
      basketToken,
      selectedCouponPromotionId: selectedCouponPromotionId ?? null,
      enableFitmatch: false,
      fitmatchScanningCapableDevice: false,
      enableAiuta: false,
      showAiutaGeneratedImages: false,
      // we want to include the footer dynamic section only direct entry to the article detail page
      // and not on navigation from other pages like category or search
      includeFooterDynamic: isInitialLocation,
      withBasicProductInfoOnly: false,
    },
    {
      // this request should only execute on client if basket token is there
      // This request should always have basket token in it
      // Product condition was added because of https://aboutyou.atlassian.net/browse/ASFE-9954 (inline size selection AB test)
      // AB hook needs this values from this to return true, and that AB test hook is used in app install banner.
      // We dont want it to run anywhere where product id is not available
      disabled: refetchesDisabled || !productId || disabled,
      keepPreviousData,
      keepPreviousDataOnClientError: true,
      section,
      refetchOnWindowFocus: true,
      staleTime: FIVE_MINUTES_IN_MS,
      transformCacheKey,
      onSuccess,
    },
  );

  useEffect(() => {
    // since this is a BE abtest we can activate only after the request is done
    // NOTE: we show the optimistic UI from history entities even before the BE request resolve
    if (request.isLoading === false) {
      activateAATest();
    }
  }, [activateAATest, request.isLoading]);

  return request;
};

const transformCacheKey = (config: CacheKeyConfig) => {
  return {
    ...config,
    params: {
      ...config.params,
      // ignore the preferred product image and potentially reuse the existing key to prevent multiple requests to the backend and hydration issues
      preferredProductImage: ProductImageTypes.UNSPECIFIED,
    },
  };
};

/**
 * On direct entry returns the user preferred product image type from the cookie.
 * No route entities are considered not to cause hydration errors.
 *
 * On navigation from for example the category page to the ADP route entities are considered
 * as the backend might set the product image type which needs to be considered.
 */
const usePreferredProductImage = () => {
  const productImageTypeCallback = useProductImageTypeCallback();
  const isInitialLocation = useIsInitialLocation();
  const isQuickView = useIsQuickView();
  const {preferredProductImageType} =
    useRouteEntities<ProductTileEntities>() as ProductTileEntities;

  if (!isInitialLocation && preferredProductImageType !== undefined) {
    return preferredProductImageType;
  }

  return getProtoProductImageType(
    isQuickView ? 'product' : productImageTypeCallback(),
  );
};

export const useImagesSection = (disabled?: boolean) => {
  return useGetProductBulkSection({section: 'imagesSection', disabled});
};

// productId is only added to match the signature of the useGetBadgesFromMinimalImagesSection hook
// since we conditionally use one or the other on the add to basket section
export const useGetProductBulkBadges = ({
  disabled,
}: {
  productId?: number;
  disabled?: boolean;
}) => {
  const imagesSection = useImagesSection(disabled);
  const imagesSectionBadges = imagesSection?.data?.badges;
  const priceAndBrand = usePriceAndBrandSection(disabled);
  const combinedBadges = useGetBadgesFromBulkRequest({
    imagesSectionBadges,
    priceAndBrand: priceAndBrand.data,
  });

  return {
    badges: combinedBadges,
    isFetching: imagesSection.isFetching || priceAndBrand.isFetching,
  };
};

export const useProductDetailSection = () => {
  return useGetProductBulkSection({section: 'productDetailsSection'});
};

export const useBasketTokenSection = (
  onSuccess?: (data: GetProductBulkResponse) => void,
) => {
  return useGetProductBulkSection({section: 'basketToken', onSuccess});
};

export const useSizesSection = ({
  currentSize,
  disabled,
  productId,
}: {
  currentSize?: {
    selectedSize: SelectedSize;
    setSelectedSize: SetSelectedSize;
  };
  disabled?: boolean;
  productId?: number;
} = {}) => {
  const request = useGetProductBulkSection({
    section: 'sizesSection',
    disabled,
    productId,
  });

  useEffect(() => {
    if (!currentSize || !request?.data) {
      return;
    }

    const {selectedSize, setSelectedSize} = currentSize;
    const updatedSize = getCurrentSelectedSizeFromSizesSection(
      request.data,
      selectedSize,
    );

    if (updatedSize) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setSelectedSize(updatedSize);
    }
  }, [currentSize?.selectedSize?.sizeId, request?.data]);

  return request;
};

export const useMerchantBannerSection = () => {
  return useGetProductBulkSection({section: 'merchantBannerSection'});
};

export const usePriceAndBrandSection = (disabled?: boolean) => {
  return useGetProductBulkSection({
    section: 'priceAndBrandSection',
    disabled,
  });
};

export const useProductSelectionSection = () => {
  return useGetProductBulkSection({section: 'productSelectionSection'});
};

export const useUSPsSection = () => {
  return useGetProductBulkSection({section: 'uspsSection'});
};

export const useLinksSection = (disabled?: boolean) => {
  return useGetProductBulkSection({section: 'linksSection', disabled});
};

export const useBASPlacementSection = () => {
  return useGetProductBulkSection({section: 'basPlacementSection'});
};

export const useReviewsSection = () => {
  return useGetProductBulkSection({section: 'reviewSection'});
};

export const useProminentBannerSection = () => {
  return useGetProductBulkSection({section: 'prominentBannerSection'});
};

export const useBestPriceHintSection = () => {
  return useGetProductBulkSection({section: 'bestPriceHintSection'});
};

export const useHotProductSection = () => {
  return useGetProductBulkSection({section: 'hotProductSection'});
};

export const useLegalReportSection = () => {
  return useGetProductBulkSection({section: 'legalReportSection'});
};

export const useCoinsBannerSection = () => {
  return useGetProductBulkSection({section: 'coinsBannerSection'});
};

export const useFooterDynamicSection = () => {
  return useGetProductBulkSection({section: 'dynamicFooterSection'});
};
